import { shouldSwapDimensions } from '@v2/components/imageHandling/helpers';
import {
  Annotation,
  AnnotationPoint,
  AnnotationType,
  ArrowAnnotation,
  CircleAnnotation,
  HighlighterAnnotation,
  PenAnnotation,
  RebaseCoordinatesParams,
  RectangleAnnotation,
  TextAnnotation,
} from '../types';

/** Inverse of `imageToScreen` in fromOriginalCoordinates. */
const screenToImage = (
  s: AnnotationPoint,
  image: { widthOriginal: number; heightOriginal: number },
  viewport: { viewportWidth: number; viewportHeight: number },
  rotation: number
): AnnotationPoint => {
  const { widthOriginal, heightOriginal } = image;
  const { viewportWidth, viewportHeight } = viewport;
  const swap = shouldSwapDimensions(rotation);
  const containerW = swap ? viewportHeight : viewportWidth;
  const containerH = swap ? viewportWidth : viewportHeight;

  // 1. Un-rotate around viewport center → pre-rotation point.
  let preRotX = s.x;
  let preRotY = s.y;
  if (rotation) {
    const cx = viewportWidth / 2;
    const cy = viewportHeight / 2;
    const rad = (-rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const dx = s.x - cx;
    const dy = s.y - cy;
    preRotX = cx + dx * cos - dy * sin;
    preRotY = cy + dx * sin + dy * cos;
  }

  // 2. Subtract container offset to get container-local coords.
  const contOffX = (viewportWidth - containerW) / 2;
  const contOffY = (viewportHeight - containerH) / 2;
  const lbX = preRotX - contOffX;
  const lbY = preRotY - contOffY;

  // 3. Un-letterbox within container.
  const scale = Math.min(containerW / widthOriginal, containerH / heightOriginal) || 1;
  const letterOffX = (containerW - widthOriginal * scale) / 2;
  const letterOffY = (containerH - heightOriginal * scale) / 2;

  return {
    x: (lbX - letterOffX) / scale,
    y: (lbY - letterOffY) / scale,
  };
};

const getDisplayScale = (
  image: { widthOriginal: number; heightOriginal: number },
  viewport: { viewportWidth: number; viewportHeight: number },
  rotation: number
) => {
  const swap = shouldSwapDimensions(rotation);
  const containerW = swap ? viewport.viewportHeight : viewport.viewportWidth;
  const containerH = swap ? viewport.viewportWidth : viewport.viewportHeight;
  return Math.min(containerW / image.widthOriginal, containerH / image.heightOriginal) || 1;
};

export const toOriginalCoordinates = ({
  annotation,
  image,
  viewport,
  documentRotation = 0,
}: RebaseCoordinatesParams): Annotation => {
  const scale = getDisplayScale(image, viewport, documentRotation);
  const toSize = (v: number) => v / scale;
  const toPoint = (p: AnnotationPoint) => screenToImage(p, image, viewport, documentRotation);

  const rebasedStyle = {
    ...annotation.style,
    strokeWidth: toSize(annotation.style.strokeWidth),
  };

  switch (annotation.type) {
    case AnnotationType.Pen:
    case AnnotationType.Highlighter: {
      const a = annotation as PenAnnotation | HighlighterAnnotation;
      return {
        ...a,
        style: rebasedStyle,
        points: a.points.map(toPoint),
      };
    }
    case AnnotationType.Arrow: {
      const a = annotation as ArrowAnnotation;
      return {
        ...a,
        style: rebasedStyle,
        start: toPoint(a.start),
        end: toPoint(a.end),
      };
    }
    case AnnotationType.Rectangle: {
      const a = annotation as RectangleAnnotation;
      const origin = toPoint(a.origin);
      const end = toPoint({ x: a.origin.x + a.width, y: a.origin.y + a.height });
      return {
        ...a,
        style: rebasedStyle,
        origin: { x: Math.min(origin.x, end.x), y: Math.min(origin.y, end.y) },
        width: Math.abs(end.x - origin.x),
        height: Math.abs(end.y - origin.y),
      };
    }
    case AnnotationType.Circle: {
      const a = annotation as CircleAnnotation;
      return {
        ...a,
        style: rebasedStyle,
        center: toPoint(a.center),
        radiusX: toSize(a.radiusX),
        radiusY: toSize(a.radiusY),
      };
    }
    case AnnotationType.Text: {
      const a = annotation as TextAnnotation;
      return {
        ...a,
        style: rebasedStyle,
        position: toPoint(a.position),
        fontSize: toSize(a.fontSize),
      };
    }
    default:
      return annotation;
  }
};
